import React, { useCallback, useEffect, useState } from 'react';
import styles from './EvaluationOptions.module.scss';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Table from '../../../components/table/Table';
import Search from '../../../components/search/Search';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import Modal from '../../../components/modal/Modal';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';

const EvaluationOptions = () => {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1, total: 0, start: 1, end: 1, hasPrev: false, hasNext: false
  });

  const fetchOptions = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = { page, limit: 10, search };
      const response = await axiosClient.get('/options', { params });
      const responseData = response.data;
      setData(responseData.options || []);
      const totalPages = Math.ceil(responseData.total / responseData.limit);
      setPagination({
        current: responseData.page,
        total: responseData.total,
        start: (responseData.page - 1) * responseData.limit + 1,
        end: Math.min(responseData.page * responseData.limit, responseData.total),
        hasPrev: responseData.page > 1,
        hasNext: responseData.page < totalPages
      });
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast, search]);

  useEffect(() => {
    fetchOptions(1);
  }, [fetchOptions]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axiosClient.delete(`/options/${deletingId}`);
      showToast('Option deleted successfully');
      setDeletingId(null);
      fetchOptions(pagination.current);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      header: 'Name',
      key: 'name',
      render: (row) => (
        <span className={styles.nameLink} onClick={() => navigate(`/evaluation-options/edit/${row.id}`)}>
          {row.name}
        </span>
      ),
    },
    {
      header: 'Description',
      key: 'description',
      render: (row) => (
        <span className={styles.description} title={row.description}>
          {row.description}
        </span>
      ),
    },
    { header: 'Point', key: 'point' },
    {
      header: 'Min %',
      key: 'minPercentage',
      render: (row) => parseFloat(row.minPercentage).toFixed(1),
    },
    {
      header: 'Max %',
      key: 'maxPercentage',
      render: (row) => parseFloat(row.maxPercentage).toFixed(1),
    },
  ];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Evaluation Options" />
        <div className={styles.headerActions}>
          <Search onSubmit={(value) => setSearch(value)} />
          <Button
            label={<div className={styles.cta}><Icon name="plus" />Create Option</div>}
            variant="primary"
            size="medium"
            onClick={() => navigate('/evaluation-options/create')}
          />
        </div>
      </div>
      <div className={styles.container}>
        <Table
          columns={columns}
          data={data}
          pagination={pagination}
          onPageChange={(page) => fetchOptions(page)}
          emptyMessage="No options found."
          getActionItems={(row) => [
            { label: 'Edit', onClick: () => navigate(`/evaluation-options/edit/${row.id}`) },
            { label: 'Delete', onClick: () => setDeletingId(row.id) },
          ]}
        />
      </div>

      {deletingId && (
        <Modal>
          <div className={styles.confirmModal}>
            {isDeleting && <LoadingOverlay />}
            <h2 className={styles.confirmTitle}>Are you sure you want to delete this option?</h2>
            <div className={styles.confirmButtons}>
              <Button label="Delete" size="small" variant="secondary" onClick={handleDelete} disabled={isDeleting} />
              <Button label="Cancel" size="small" variant="cancel" onClick={() => setDeletingId(null)} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default EvaluationOptions;
